import { Fragment, useEffect, useRef, useState } from 'react';
import { loadConfig, type AppConfig } from './config';
import {
  beginPasswordless,
  getCurrentSession,
  signInWithPassword,
  signOut,
  type Session,
} from './auth';
import {
  addStemsToVersion,
  adminGetUserProjects,
  adminListProjects,
  adminListUsers,
  deleteProject,
  applyApproval,
  createProject,
  createStemVersion,
  createVersion,
  deleteStemAsset,
  exportComments,
  getStemDownloadUrl,
  getVersion,
  inviteMember,
  listMembers,
  listMyProjects,
  listActivity,
  listProjectTracks,
  listTrackPermissions,
  listTrackVersions,
  removeMember,
  removeTrackPermission,
  setMemberRole,
  setTrackPermission,
  updateProject,
  uploadToPresignedUrl,
  RETENTION_MIN_DAYS,
  RETENTION_MAX_DAYS,
  type ActivityEvent,
  type ApprovalDecision,
  type ExportFile,
  type Member,
  type MemberRole,
  type Project,
  type AdminUser,
  type AdminUserProject,
  type AdminProject,
  type ProjectTrack,
  type StemUpload,
  type TrackOverride,
  type VersionAsset,
  type VersionSummary,
} from './api';
import { TrackPlayer } from './TrackPlayer';
import { StemsPlayer } from './StemsPlayer';
import { t, Btn, Card, Badge, SectionTitle, Muted, Icon, BRAND_NAME } from './ui';
import {
  buildPrecheckNotices,
  isWav,
  readWavHeader,
  type PrecheckNotice,
  type SetStemRef,
} from './stemPrecheck';

// Navigation shell: Projects → Tracks → Track detail (versions + unified player + comments).
// The track-detail view hosts the version overview, the approval/export review panel, and the
// unified TrackPlayer (one transport + A/B/C version switch + comments on the audible version),
// plus "add a version" upload.

export function App() {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadConfig()
      .then(async (c) => {
        setConfig(c);
        setSession(await getCurrentSession(c));
      })
      .catch((e) => setError(String(e)));
  }, []);

  if (error) return <Shell><p className="error">{error}</p></Shell>;
  if (!config) return <Shell><p>Loading…</p></Shell>;
  if (!session) return <Shell><SignIn config={config} onSignedIn={setSession} /></Shell>;

  return <SignedInApp config={config} session={session} onSignOut={() => { signOut(config); setSession(null); }} />;
}

// Top-level signed-in shell: the project browser, plus admin-only "Users" and "All projects"
// consoles. `mode` selects which surface is shown. Opening a project from the admin list sets the
// deep-link hash and drops back into the Browser, whose hydrate-on-mount reads that hash and opens
// straight to the chosen project (admins already have access to every project server-side).
type ViewMode = 'browser' | 'admin-users' | 'admin-projects';

function SignedInApp({ config, session, onSignOut }: { config: AppConfig; session: Session; onSignOut: () => void }) {
  const [mode, setMode] = useState<ViewMode>('browser');
  // Remount key for Browser: bumping it forces a fresh mount so the hash-hydrate effect re-runs
  // when an admin opens a project from the All-projects list.
  const [browserKey, setBrowserKey] = useState(0);

  const openProject = (projectId: string) => {
    window.location.hash = `#/project/${projectId}`;
    setBrowserKey((k) => k + 1);
    setMode('browser');
  };

  const actions = (
    <>
      {session.email && <Muted style={{ fontSize: 13 }}>{session.email}</Muted>}
      {session.isAdmin && mode !== 'browser' && (
        <Btn variant="ghost" onClick={() => { window.location.hash = ''; setMode('browser'); }}>← My projects</Btn>
      )}
      {session.isAdmin && mode === 'browser' && (
        <>
          <Btn variant="ghost" onClick={() => setMode('admin-projects')}>All projects (admin)</Btn>
          <Btn variant="ghost" onClick={() => setMode('admin-users')}>Users (admin)</Btn>
        </>
      )}
      <Btn variant="secondary" onClick={onSignOut}>Sign out</Btn>
    </>
  );

  return (
    <Shell actions={actions}>
      {mode === 'admin-users' && session.isAdmin && <AdminUsersView config={config} session={session} />}
      {mode === 'admin-projects' && session.isAdmin && (
        <AdminProjectsView config={config} session={session} onOpen={openProject} />
      )}
      {mode === 'browser' && <Browser key={browserKey} config={config} session={session} />}
    </Shell>
  );
}

// Branded page frame: a slim fixed top bar (logo + brand name + optional right-hand actions),
// then a centered content column. Mirrors the marketing site's translucent nav.
function Shell({ children, actions }: { children: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: t.bg, color: t.text }}>
      <header
        style={{
          position: 'sticky', top: 0, zIndex: 100,
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 20px',
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${t.border}`,
        }}
      >
        <img src="/logo.png" alt="" width={30} height={30} style={{ borderRadius: 7, objectFit: 'cover' }} />
        <span style={{ fontWeight: 600, fontSize: 15, letterSpacing: '-0.01em' }}>{BRAND_NAME}</span>
        {actions && <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>{actions}</div>}
      </header>
      <main style={{ maxWidth: 960, margin: '0 auto', padding: '24px 20px 64px' }}>
        {children}
      </main>
    </div>
  );
}

// ---- Navigation ------------------------------------------------------------

type Nav =
  | { view: 'projects' }
  | { view: 'tracks'; project: Project }
  | { view: 'members'; project: Project }
  | { view: 'track'; project: Project; trackId: string; trackName: string }
  | { view: 'stems'; project: Project; trackId: string; trackName: string };

// Deep-link routing (Feature 1): the app is a single view with in-memory nav, but email
// notifications link to #/project/<projectId>/track/<trackId>. We parse that hash on load and
// hydrate the full nav (fetching the project + track name/kind), and mirror nav → hash so the
// address bar is shareable. Only track/stems views carry a deep hash; everything else is root.
type DeepLink = { projectId: string; trackId?: string };

function parseHash(hash: string): DeepLink | null {
  const m = /^#\/project\/([^/]+)(?:\/(?:track|stems)\/([^/]+))?/.exec(hash || '');
  if (!m) return null;
  return { projectId: decodeURIComponent(m[1]), trackId: m[2] ? decodeURIComponent(m[2]) : undefined };
}

function navToHash(nav: Nav): string {
  if (nav.view === 'track' || nav.view === 'stems') {
    return `#/project/${nav.project.projectId}/track/${nav.trackId}`;
  }
  if (nav.view === 'tracks' || nav.view === 'members') {
    return `#/project/${nav.project.projectId}`;
  }
  return '';
}

function Browser({ config, session }: { config: AppConfig; session: Session }) {
  const [nav, setNavState] = useState<Nav>({ view: 'projects' });
  const [hydrating, setHydrating] = useState(false);

  // Wrap setNav so every navigation mirrors into the URL hash (replaceState = no history spam).
  const setNav = (next: Nav) => {
    setNavState(next);
    const hash = navToHash(next);
    const url = hash || window.location.pathname + window.location.search;
    window.history.replaceState(null, '', url);
  };

  // On first load, if the URL carries a deep-link, hydrate the nav from the IDs. Requires fetching
  // the project (for the full Project object the track/stems views need) and the track (for its
  // name + mix/stems kind). Best-effort: on any miss we fall back to the projects list.
  useEffect(() => {
    const dl = parseHash(window.location.hash);
    if (!dl) return;
    let cancelled = false;
    setHydrating(true);
    (async () => {
      try {
        // Resolve the project via listProjectTracks (gated by `view`, which admins and members
        // both pass) rather than listMyProjects — so this works for an admin opening a project
        // they're NOT a member of, and for members following an email deep-link. We enrich the
        // Project with the caller's own membership row when present (for real permissions/role);
        // admins get the full permission set so the manage/upload UI is available.
        const [tracksRes, mine] = await Promise.all([
          listProjectTracks(config, session, dl.projectId),
          listMyProjects(config, session).catch(() => ({ projects: [] as Project[] })),
        ]);
        if (cancelled) return;
        const membership = mine.projects.find((p) => p.projectId === dl.projectId);
        const ALL_PERMS = ['view', 'comment', 'approve', 'download', 'upload_version', 'manage'];
        const project: Project = {
          projectId: dl.projectId,
          name: tracksRes.name,
          type: tracksRes.type,
          loudnessRef: tracksRes.loudnessRef ?? -14,
          retentionDays: tracksRes.retentionDays ?? 60,
          role: membership?.role ?? (session.isAdmin ? 'admin' : 'viewer'),
          permissions: membership?.permissions ?? (session.isAdmin ? ALL_PERMS : []),
          createdAt: membership?.createdAt ?? null,
        };
        if (!dl.trackId) {
          setNavState({ view: 'tracks', project });
          return;
        }
        const track = tracksRes.tracks.find((tr) => tr.trackId === dl.trackId);
        if (!track) {
          setNavState({ view: 'tracks', project });
          return;
        }
        setNavState({
          view: track.kind === 'stems' ? 'stems' : 'track',
          project,
          trackId: track.trackId,
          trackName: track.name,
        });
      } catch {
        /* fall back to the projects list (initial state) */
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => { cancelled = true; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  if (hydrating) {
    return <Muted style={{ display: 'block', marginTop: 12 }}>Opening…</Muted>;
  }

  const crumb = (
    <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 12 }}>
      <button onClick={() => setNav({ view: 'projects' })} style={crumbBtn}>My projects</button>
      {nav.view !== 'projects' && (
        <>
          {' / '}
          <button
            onClick={() => setNav({ view: 'tracks', project: nav.project })}
            style={crumbBtn}
          >
            {nav.project.name}
          </button>
        </>
      )}
      {(nav.view === 'track' || nav.view === 'stems') && <> {' / '}<span>{nav.trackName}</span></>}
      {nav.view === 'members' && <> {' / '}<span>Manage project</span></>}
    </div>
  );

  return (
    <div>
      {crumb}
      {nav.view === 'projects' && (
        <ProjectsView
          config={config}
          session={session}
          onOpen={(project) => setNav({ view: 'tracks', project })}
        />
      )}
      {nav.view === 'tracks' && (
        <TracksView
          config={config}
          session={session}
          project={nav.project}
          onOpen={(trackId, trackName, kind) =>
            setNav({
              view: kind === 'stems' ? 'stems' : 'track',
              project: nav.project,
              trackId,
              trackName,
            })}
          onManage={() => setNav({ view: 'members', project: nav.project })}
        />
      )}
      {nav.view === 'members' && (
        <MembersView config={config} session={session} project={nav.project} />
      )}
      {nav.view === 'track' && (
        <TrackDetail
          config={config}
          session={session}
          project={nav.project}
          trackId={nav.trackId}
          trackName={nav.trackName}
        />
      )}
      {nav.view === 'stems' && (
        <StemsView
          config={config}
          session={session}
          project={nav.project}
          trackId={nav.trackId}
          trackName={nav.trackName}
        />
      )}
    </div>
  );
}

const crumbBtn: React.CSSProperties = {
  border: 'none', background: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 0, font: 'inherit',
};

// Prominent pre-upload advisory block. Non-blocking, but visually unmissable so an obvious
// mistake (wrong format / rate / length) is caught before bytes are uploaded. Amber for warnings,
// blue for info (e.g. the WAV recommendation). Renders nothing when there are no notices.
function PrecheckNotices({ notices }: { notices: PrecheckNotice[] }) {
  if (notices.length === 0) return null;
  const hasWarn = notices.some((n) => n.severity === 'warn');
  const edge = hasWarn ? 'rgba(224,165,75,0.5)' : 'rgba(77,184,184,0.4)';
  const fill = hasWarn ? 'rgba(224,165,75,0.10)' : 'rgba(77,184,184,0.08)';
  return (
    <div style={{ border: `1px solid ${edge}`, background: fill, borderRadius: 'var(--radius-sm)', padding: '8px 10px', margin: '8px 0' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Before you upload</div>
      {notices.map((n, i) => (
        <div key={i} style={{ fontSize: 12, color: n.severity === 'warn' ? 'var(--warn)' : 'var(--accent)', margin: '2px 0' }}>
          {n.severity === 'warn' ? '⚠ ' : 'ℹ '}{n.message}
        </div>
      ))}
    </div>
  );
}

// ---- Admin: all-projects console (admin-only) ------------------------------
// Lists every project in the system so an admin can discover and open any of them, even without
// being a member. Opening a row hands the projectId up to SignedInApp, which deep-links the
// Browser to it (admins already hold access server-side via the Cognito `admin` group).

function AdminProjectsView({
  config, session, onOpen,
}: { config: AppConfig; session: Session; onOpen: (projectId: string) => void }) {
  const [projects, setProjects] = useState<AdminProject[] | null>(null);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Delete flow: the project pending deletion, the typed confirmation, and a status line.
  const [pendingDelete, setPendingDelete] = useState<AdminProject | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState<string | null>(null);

  const load = async (token?: string) => {
    setBusy(true); setError(null);
    try {
      const res = await adminListProjects(config, session, token ? { paginationToken: token } : {});
      setProjects((prev) => (token && prev ? [...prev, ...res.projects] : res.projects));
      setNextToken(res.nextToken);
    } catch (e) { setError(String(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const startDelete = (p: AdminProject) => {
    setPendingDelete(p);
    setConfirmText('');
    setDeleteMsg(null);
  };

  // The exact string the admin must type to arm the delete (the project name, or the id if unnamed).
  const confirmTarget = pendingDelete ? (pendingDelete.name || pendingDelete.projectId) : '';
  const confirmArmed = !!pendingDelete && confirmText.trim() === confirmTarget;

  const doDelete = async () => {
    if (!pendingDelete || !confirmArmed) return;
    setDeleteBusy(true); setDeleteMsg(null);
    try {
      const res = await deleteProject(config, session, pendingDelete.projectId);
      setProjects((prev) => (prev ? prev.filter((x) => x.projectId !== pendingDelete.projectId) : prev));
      setDeleteMsg(`Deleted "${res.name || res.projectId}" — ${res.versions} version(s), ${res.deletedS3Objects} file(s), ${res.deletedDbItems} record(s) removed.`);
      setPendingDelete(null);
      setConfirmText('');
    } catch (e) { setDeleteMsg(`Error: ${String(e)}`); }
    finally { setDeleteBusy(false); }
  };

  return (
    <div>
      <h2>All projects <span style={{ fontSize: 13, color: t.muted, fontWeight: 'normal' }}>· admin</span></h2>
      <p style={{ fontSize: 12, color: t.muted, margin: '0 0 12px' }}>
        Every project in the system. Click a name to open it — you have full access as an admin even
        if you aren’t a listed member. Delete is permanent and removes all of a project’s files.
      </p>
      {projects === null && <Muted style={{ display: 'block' }}>Loading…</Muted>}
      {projects && projects.length === 0 && <Muted style={{ display: 'block' }}>No projects.</Muted>}
      {projects && projects.length > 0 && (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: `1px solid ${t.border}`, fontSize: 12, color: t.muted }}>
                <th style={{ padding: '10px 12px', fontWeight: 600 }}>Project</th>
                <th style={{ padding: '10px 12px', fontWeight: 600 }}>Type</th>
                <th style={{ padding: '10px 12px', fontWeight: 600 }}>Created by</th>
                <th style={{ padding: '10px 12px', fontWeight: 600 }}>Created</th>
                <th style={{ padding: '10px 12px' }}></th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.projectId} style={{ borderBottom: `1px solid ${t.border}` }}>
                  <td style={{ padding: '10px 12px' }}>
                    <button onClick={() => onOpen(p.projectId)} style={{ ...crumbBtn, fontWeight: 600 }}>
                      {p.name || p.projectId}
                    </button>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <Badge tone="neutral">{p.type === 'MULTITRACK' ? 'Multitrack' : 'Mix review'}</Badge>
                  </td>
                  <td style={{ padding: '10px 12px', color: t.muted, fontSize: 13 }}>{p.createdByEmail || p.createdBy || '—'}</td>
                  <td style={{ padding: '10px 12px', color: t.muted, fontSize: 13 }}>{fmtDate(p.createdAt)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <Btn variant="danger" onClick={() => startDelete(p)} icon={<Icon.x />} iconOnly
                      title="Delete project" aria-label={`Delete project ${p.name || p.projectId}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      {nextToken && (
        <Btn variant="secondary" onClick={() => load(nextToken)} disabled={busy} style={{ marginTop: 12 }}>
          {busy ? 'Loading…' : 'Load more'}
        </Btn>
      )}
      {deleteMsg && (
        <p style={{ fontSize: 13, color: deleteMsg.startsWith('Error') ? t.danger : t.accent, marginTop: 12 }}>{deleteMsg}</p>
      )}
      {error && <p style={{ color: t.danger }}>{error}</p>}

      {/* Type-to-confirm delete panel — irreversible, so arm only on an exact name match. */}
      {pendingDelete && (
        <Card style={{ marginTop: 16, borderColor: 'rgba(229,103,91,0.5)' }}>
          <strong style={{ fontSize: 14, color: t.danger }}>Delete “{confirmTarget}”?</strong>
          <p style={{ fontSize: 13, color: t.muted, margin: '8px 0' }}>
            This permanently removes the project and <strong>all</strong> of its tracks, versions,
            audio files, comments, and members. This cannot be undone. Type the project name to confirm.
          </p>
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setPendingDelete(null); setConfirmText(''); }
              if (e.key === 'Enter' && confirmArmed && !deleteBusy) doDelete();
            }}
            placeholder={confirmTarget}
            aria-label="Type the project name to confirm deletion"
            autoFocus
            style={{ width: '100%', maxWidth: 360 }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Btn variant="danger" onClick={doDelete} disabled={!confirmArmed || deleteBusy}>
              {deleteBusy ? 'Deleting…' : 'Delete permanently'}
            </Btn>
            <Btn variant="ghost" onClick={() => { setPendingDelete(null); setConfirmText(''); }} disabled={deleteBusy}>
              Cancel
            </Btn>
          </div>
        </Card>
      )}
    </div>
  );
}

// ---- Admin: all-users console (read-only, admin-only) ----------------------

function AdminUsersView({ config, session }: { config: AppConfig; session: Session }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Expanded user → their project memberships (lazy-loaded on first expand).
  const [expanded, setExpanded] = useState<string | null>(null);
  const [projectsByUser, setProjectsByUser] = useState<Record<string, AdminUserProject[]>>({});

  const load = async (token?: string) => {
    setBusy(true); setError(null);
    try {
      const res = await adminListUsers(config, session, token ? { paginationToken: token } : {});
      setUsers((prev) => (token && prev ? [...prev, ...res.users] : res.users));
      setNextToken(res.nextToken);
    } catch (e) { setError(String(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const toggle = async (u: AdminUser) => {
    if (!u.sub) return;
    if (expanded === u.sub) { setExpanded(null); return; }
    setExpanded(u.sub);
    if (!projectsByUser[u.sub]) {
      try {
        const res = await adminGetUserProjects(config, session, u.sub);
        setProjectsByUser((prev) => ({ ...prev, [u.sub as string]: res.projects }));
      } catch (e) { setError(String(e)); }
    }
  };

  return (
    <div>
      <h2>All users <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 'normal' }}>· admin</span></h2>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 12px' }}>
        Every user in the pool. Click a user to see the projects they belong to. Read-only for now.
      </p>
      {users === null && <Muted style={{ display: 'block' }}>Loading…</Muted>}
      {users && users.length === 0 && <Muted style={{ display: 'block' }}>No users.</Muted>}
      {users && users.length > 0 && (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: `1px solid ${t.border}`, fontSize: 12, color: t.muted }}>
                <th style={{ padding: '10px 12px', fontWeight: 600 }}>Email</th>
                <th style={{ padding: '10px 12px', fontWeight: 600 }}>Status</th>
                <th style={{ padding: '10px 12px', fontWeight: 600 }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <Fragment key={u.sub || u.email || Math.random()}>
                  <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                    <td style={{ padding: '10px 12px' }}>
                      <button onClick={() => toggle(u)} style={crumbBtn}>
                        {expanded === u.sub ? '▾ ' : '▸ '}{u.email || u.sub}
                      </button>
                      {!u.enabled && <Badge tone="danger">disabled</Badge>}
                    </td>
                    <td style={{ padding: '10px 12px', color: t.muted, fontSize: 13 }}>{u.status || '—'}</td>
                    <td style={{ padding: '10px 12px', color: t.muted, fontSize: 13 }}>{fmtDate(u.createdAt)}</td>
                  </tr>
                  {expanded === u.sub && (
                    <tr>
                      <td colSpan={3} style={{ padding: '0 12px 10px 28px' }}>
                        {projectsByUser[u.sub as string] === undefined && <span style={{ fontSize: 12, color: t.muted }}>Loading memberships…</span>}
                        {projectsByUser[u.sub as string]?.length === 0 && <span style={{ fontSize: 12, color: t.muted }}>No project memberships.</span>}
                        {projectsByUser[u.sub as string]?.length > 0 && (
                          <ul style={{ fontSize: 13, margin: '4px 0' }}>
                            {projectsByUser[u.sub as string].map((p) => (
                              <li key={p.projectId}>
                                {p.name || p.projectId} — <span style={{ color: t.accent }}>{p.role}</span>
                                <span style={{ color: t.faint, fontSize: 12 }}> ({p.permissions.join(', ')})</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      {nextToken && (
        <Btn variant="secondary" onClick={() => load(nextToken)} disabled={busy} style={{ marginTop: 12 }}>
          {busy ? 'Loading…' : 'Load more'}
        </Btn>
      )}
      {error && <p style={{ color: t.danger }}>{error}</p>}
    </div>
  );
}

// ---- Projects --------------------------------------------------------------

function ProjectsView({
  config, session, onOpen,
}: { config: AppConfig; session: Session; onOpen: (p: Project) => void }) {
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try { setProjects((await listMyProjects(config, session)).projects); }
    catch (e) { setError(String(e)); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true); setError(null);
    try {
      const p = await createProject(config, session, { name });
      setNewName('');
      await refresh();
      onOpen(p);
    } catch (e) { setError(String(e)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>My projects</h2>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New project name"
          aria-label="New project name"
          style={{ flex: 1, maxWidth: 340 }}
          onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
        />
        <Btn variant="primary" icon={<Icon.plus />} onClick={create} disabled={busy || !newName.trim()}>Create project</Btn>
      </div>
      {projects === null && <Muted>Loading…</Muted>}
      {projects && projects.length === 0 && <Muted>No projects yet. Create one above.</Muted>}
      {projects && projects.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
          {projects.map((p) => (
            <Card key={p.projectId} hover onClick={() => onOpen(p)}
              style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span aria-hidden style={{ color: t.accent, display: 'inline-flex' }}><Icon.folder /></span>
                <span style={{ fontWeight: 600, fontSize: 15 }}>{p.name}</span>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <Badge tone="neutral">{p.type === 'MULTITRACK' ? 'Multitrack' : 'Mix review'}</Badge>
                <Badge tone="accent">{p.role}</Badge>
              </div>
            </Card>
          ))}
        </div>
      )}
      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
    </div>
  );
}

// ---- Tracks in a project ---------------------------------------------------

function TracksView({
  config, session, project, onOpen, onManage,
}: {
  config: AppConfig; session: Session; project: Project;
  onOpen: (trackId: string, trackName: string, kind: 'mix' | 'stems') => void;
  onManage: () => void;
}) {
  const canManage = (project.permissions || []).includes('manage');
  const canUpload = (project.permissions || []).includes('upload_version');
  const [tracks, setTracks] = useState<ProjectTrack[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // New mix track (single file).
  const [file, setFile] = useState<File | null>(null);
  const [trackName, setTrackName] = useState('');
  const [status, setStatus] = useState('');

  // New stem set (multiple files → one stems version).
  const [stemFiles, setStemFiles] = useState<File[]>([]);
  const [stemTrackName, setStemTrackName] = useState('');
  const [stemStatus, setStemStatus] = useState('');
  const [stemNotices, setStemNotices] = useState<PrecheckNotice[]>([]);

  // On select: run WAV-header + format-consistency pre-checks across the chosen files. A brand-new
  // set has no prior stems, so the reference is the selected files compared against each other
  // (plus a WAV recommendation for any non-WAV). Advisory only — never blocks the upload.
  const onSelectStemFiles = async (files: File[]) => {
    setStemFiles(files);
    setStemNotices([]);
    if (files.length === 0) return;
    const notices: PrecheckNotice[] = [];
    const nonWav = files.filter((f) => !isWav(f.name));
    if (nonWav.length > 0) {
      notices.push({
        severity: 'info',
        message: `We recommend WAV for stems (lossless, exact length). ${nonWav.length} of ${files.length} selected ${nonWav.length === 1 ? 'is' : 'are'} not WAV — they'll still upload, with limited format checks until after analysis.`,
      });
    }
    // Compare the WAV files against each other (use the first readable WAV as the reference set).
    const headers = await Promise.all(files.map((f) => (isWav(f.name) ? readWavHeader(f) : Promise.resolve(null))));
    // Per-file reference (null-aligned to `files`), so excluding self by index stays correct.
    const refs: (SetStemRef | null)[] = headers.map((h) =>
      h ? { sampleRateHz: h.sampleRateHz, bitDepth: h.bitDepth, channels: h.channels, durationMs: null, sizeBytes: null } : null
    );
    files.forEach((f, i) => {
      // Compare each file against the OTHERS (exclude itself so it isn't its own reference).
      const others = refs.filter((r, j) => r != null && j !== i) as SetStemRef[];
      buildPrecheckNotices(f, headers[i], others).forEach((n) => {
        // De-dupe the generic non-WAV line (already added above) — keep only mismatch/size notices.
        if (!n.message.startsWith('We recommend') && !/isn't a WAV/.test(n.message)) notices.push(n);
      });
    });
    setStemNotices(notices);
  };

  const refresh = async () => {
    try { setTracks((await listProjectTracks(config, session, project.projectId)).tracks); }
    catch (e) { setError(String(e)); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [project.projectId]);

  const uploadNewTrack = async () => {
    if (!file) return;
    setStatus('Requesting upload URL…');
    try {
      const created = await createVersion(config, session, project.projectId, {
        filename: file.name,
        sizeBytes: file.size,
        trackName: trackName || file.name,
      });
      setStatus('Uploading…');
      await uploadToPresignedUrl(created.uploadUrl, file);
      setStatus('Uploaded — analyzing. Open the mix to watch ingest.');
      setFile(null); setTrackName('');
      await refresh();
      onOpen(created.trackId, trackName || file.name, 'mix');
    } catch (e) { setStatus(`Error: ${String(e)}`); }
  };

  const uploadNewStemSet = async () => {
    if (stemFiles.length === 0) return;
    const name = stemTrackName.trim();
    if (!name) { setStemStatus('Give the stem set a name first.'); return; }
    setStemStatus('Requesting upload URLs…');
    try {
      const stems: StemUpload[] = stemFiles.map((f) => ({ filename: f.name, sizeBytes: f.size }));
      const created = await createStemVersion(config, session, project.projectId, {
        stems, trackName: name,
      });
      // Upload each stem to its own presigned URL (match target → file by filename).
      const byName = new Map(stemFiles.map((f) => [f.name, f]));
      setStemStatus(`Uploading ${created.assets.length} stems…`);
      await Promise.all(
        created.assets.map((a) => {
          const f = byName.get(a.filename);
          return f ? uploadToPresignedUrl(a.uploadUrl, f) : Promise.resolve();
        })
      );
      setStemStatus('Uploaded — analyzing. Open the stem set to watch ingest.');
      setStemFiles([]); setStemTrackName('');
      await refresh();
      onOpen(created.trackId, name, 'stems');
    } catch (e) { setStemStatus(`Error: ${String(e)}`); }
  };

  const mixes = (tracks || []).filter((t) => t.kind !== 'stems');
  const stems = (tracks || []).filter((t) => t.kind === 'stems');

  // Track cards. Mixes use the accent (teal); stems use a distinct hue so the two sections read
  // apart at a glance. An approved track shows a green status badge.
  const trackCards = (rows: ProjectTrack[], kind: 'mix' | 'stems') => {
    const hue = kind === 'stems' ? t.warn : t.accent;
    const Ico = kind === 'stems' ? Icon.stems : Icon.mix;
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
        {rows.map((tr) => (
          <Card key={tr.trackId} hover onClick={() => onOpen(tr.trackId, tr.name, kind)}
            style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span aria-hidden style={{ color: hue, display: 'inline-flex' }}><Ico /></span>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{tr.name}</span>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              {tr.approvedVersionId
                ? <Badge tone="ok">✓ Approved</Badge>
                : <Badge tone="draft">In review</Badge>}
              <Muted>{tr.versionCount} version{tr.versionCount === 1 ? '' : 's'}</Muted>
            </div>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <h2 style={{ margin: 0 }}>{project.name}</h2>
        {canManage && (
          <Btn variant="secondary" icon={<Icon.members />} onClick={onManage}>Manage project</Btn>
        )}
      </div>

      {/* Retention notice — visible to every member (read-only). Managers edit it in Members. */}
      {typeof project.retentionDays === 'number' && (
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 0' }}>
          Audio &amp; comments are kept for <strong>{project.retentionDays} days</strong> after upload.
          {canManage && <> Change this under <button onClick={onManage} style={{ ...crumbBtn, fontSize: 12 }}>Manage project</button>.</>}
        </p>
      )}

      {tracks === null && <Muted style={{ display: 'block', marginTop: 16 }}>Loading…</Muted>}

      {/* ---- Mixes ---- */}
      <SectionTitle icon={<Icon.mix />} accent={t.accent}>Mixes</SectionTitle>
      <Muted style={{ display: 'block', margin: '-6px 0 10px' }}>
        Rendered mixes for review — version compare (A/B/C) and approval.
      </Muted>
      {canUpload && (
        <Card style={{ margin: '0 0 14px', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
          <strong style={{ fontSize: 13 }}>New mix</strong>
          <input value={trackName} onChange={(e) => setTrackName(e.target.value)} placeholder="Mix name (e.g. Song A — Final Mix)" aria-label="Mix name" style={{ width: '100%', maxWidth: 360 }} />
          <input type="file" accept=".wav,.aiff,.aif,.flac,.mp3" onChange={(e) => setFile(e.target.files?.[0] ?? null)} aria-label="Mix audio file" />
          <Btn variant="primary" onClick={uploadNewTrack} disabled={!file}>Upload new mix</Btn>
          {status && <Muted>{status}</Muted>}
        </Card>
      )}
      {tracks && mixes.length === 0 && <Muted>No mixes yet.</Muted>}
      {mixes.length > 0 && trackCards(mixes, 'mix')}

      {/* ---- Stems ---- */}
      <SectionTitle icon={<Icon.stems />} accent={t.warn}>Stems</SectionTitle>
      <Muted style={{ display: 'block', margin: '-6px 0 10px' }}>
        Multi-track stem sets — upload a batch (drums, guitars, keys…) then add overdubs to the set.
      </Muted>
      {canUpload && (
        <Card style={{ margin: '0 0 14px', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
          <strong style={{ fontSize: 13 }}>New stem set</strong>
          <Muted>We recommend stems be WAV files (lossless, exact length — best for lining up across DAWs).</Muted>
          <input value={stemTrackName} onChange={(e) => setStemTrackName(e.target.value)} placeholder="Stem set name (e.g. Song A — Stems)" aria-label="Stem set name" style={{ width: '100%', maxWidth: 360 }} />
          <input
            type="file" multiple accept=".wav,.aiff,.aif,.flac,.mp3"
            onChange={(e) => onSelectStemFiles(Array.from(e.target.files ?? []))}
            aria-label="Stem audio files"
          />
          {stemFiles.length > 0 && (
            <Muted>{stemFiles.length} file{stemFiles.length === 1 ? '' : 's'}: {stemFiles.map((f) => f.name).join(', ')}</Muted>
          )}
          <PrecheckNotices notices={stemNotices} />
          <Btn variant="primary" onClick={uploadNewStemSet} disabled={stemFiles.length === 0}>Upload stem set</Btn>
          {stemStatus && <Muted>{stemStatus}</Muted>}
        </Card>
      )}
      {tracks && stems.length === 0 && <Muted>No stem sets yet.</Muted>}
      {stems.length > 0 && trackCards(stems, 'stems')}

      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
    </div>
  );
}

// ---- Members admin (per-project) -------------------------------------------

const MEMBER_ROLES: MemberRole[] = ['viewer', 'commenter', 'approver', 'contributor', 'collaborator', 'manager'];

// Human blurb of what each role can do (mirrors the API ROLES bundles).
const ROLE_HINT: Record<MemberRole, string> = {
  viewer: 'view only',
  commenter: 'view + comment',
  approver: 'view, comment, approve, download',
  contributor: 'view, comment, download, upload stems (delete own only)',
  collaborator: 'approve + upload (everything but manage)',
  manager: 'full control incl. manage members',
};

// ---- Project activity log (Req 2.7) ----------------------------------------
// Read-only audit trail: member changes, track-permission overrides, retention sweeps, etc.
// Newest first. Actions are mapped to plain-English phrases; the raw detail is summarised inline.

const ACTIVITY_LABELS: Record<string, string> = {
  member_invited: 'invited a member',
  member_role_changed: 'changed a member’s role',
  member_removed: 'removed a member',
  track_permission_set: 'set a track permission override',
  track_permission_removed: 'removed a track permission override',
  share_link_created: 'created a share link',
  retention_sweep_preview: 'retention sweep (preview)',
  retention_sweep_deleted: 'retention sweep deleted a version',
};

/** One-line human summary of an event's detail object (best-effort; falls back to nothing). */
function activityDetail(ev: ActivityEvent): string {
  const d = ev.detail || {};
  const parts: string[] = [];
  if (typeof d.email === 'string') parts.push(d.email);
  if (typeof d.role === 'string') parts.push(`role ${d.role}`);
  if (typeof d.ordinal === 'number') parts.push(`v${d.ordinal}`);
  if (typeof d.ageDays === 'number' && typeof d.retentionDays === 'number') {
    parts.push(`${d.ageDays}d old, window ${d.retentionDays}d`);
  }
  return parts.join(' · ');
}

function ActivityPanel({
  config, session, project,
}: {
  config: AppConfig; session: Session; project: Project;
}) {
  const [events, setEvents] = useState<ActivityEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setError(null);
    try { setEvents((await listActivity(config, session, project.projectId, { limit: 50 })).events); }
    catch (e) { setError(String(e)); }
  };
  // Lazy-load on first expand (keeps the Members view snappy).
  useEffect(() => { if (open && events === null) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open]);

  return (
    <Card style={{ padding: 12, marginBottom: 16 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', color: t.text, cursor: 'pointer', fontWeight: 600 }}
      >
        {open ? '▾' : '▸'} Activity log
      </button>
      {open && (
        <div style={{ marginTop: 8 }}>
          <Btn variant="ghost" onClick={load} style={{ marginBottom: 8 }}>Refresh</Btn>
          {events === null && <p style={{ fontSize: 13, color: t.muted }}>Loading…</p>}
          {events && events.length === 0 && <p style={{ fontSize: 13, color: t.muted }}>No activity yet.</p>}
          {events && events.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {events.map((ev) => {
                const label = ACTIVITY_LABELS[ev.action] || ev.action;
                const detail = activityDetail(ev);
                const who = ev.actor.startsWith('system:') ? ev.actor : ev.actor.slice(0, 8);
                return (
                  <li key={ev.eventId} style={{ fontSize: 13, padding: '6px 0', borderBottom: `1px solid ${t.border}` }}>
                    <span>{label}</span>
                    {detail && <span style={{ color: t.muted }}> — {detail}</span>}
                    <div style={{ fontSize: 11, color: t.muted }}>
                      {who} · {new Date(ev.createdAt).toLocaleString()}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {error && <p style={{ color: t.danger, fontSize: 13 }}>{error}</p>}
        </div>
      )}
    </Card>
  );
}

// ---- Per-project retention window (Req 10.1) -------------------------------
// A manager sets how long a project's audio + records are kept (1..120 days, default 60). This is
// DYNAMIC: lowering it can make already-uploaded versions eligible for deletion on the next daily
// sweep, so the control warns before applying a reduction. Manage-gated by its parent MembersView.

function RetentionPanel({
  config, session, project,
}: {
  config: AppConfig; session: Session; project: Project;
}) {
  const [days, setDays] = useState<number>(project.retentionDays ?? 60);
  const [saved, setSaved] = useState<number>(project.retentionDays ?? 60);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const apply = async () => {
    const next = Math.min(Math.max(Math.round(days), RETENTION_MIN_DAYS), RETENTION_MAX_DAYS);
    if (next === saved) return;
    // Warn before a REDUCTION — dynamic semantics mean older versions can be swept.
    if (next < saved && !window.confirm(
      `Reduce retention from ${saved} to ${next} days?\n\n` +
      `Versions older than ${next} days will become eligible for automatic deletion on the next ` +
      `daily sweep. This cannot be undone.`
    )) return;
    setBusy(true); setMsg('');
    try {
      const res = await updateProject(config, session, project.projectId, { retentionDays: next });
      setSaved(res.retentionDays);
      setDays(res.retentionDays);
      setMsg(`Retention set to ${res.retentionDays} days.`);
    } catch (e) { setMsg(`Error: ${String(e)}`); }
    finally { setBusy(false); }
  };

  return (
    <Card style={{ padding: 12, marginBottom: 16 }}>
      <strong>Retention</strong>
      <p style={{ fontSize: 12, color: t.muted, margin: '4px 0 8px' }}>
        How long this project’s audio and records are kept. Between {RETENTION_MIN_DAYS} and{' '}
        {RETENTION_MAX_DAYS} days. A daily sweep expires versions older than this window — lowering
        it can remove existing versions.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="number"
          min={RETENTION_MIN_DAYS}
          max={RETENTION_MAX_DAYS}
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          style={{ width: 80 }}
          disabled={busy}
          aria-label="Retention window in days"
        />
        <span style={{ fontSize: 13, color: t.muted }}>days</span>
        <Btn variant="primary" onClick={apply} disabled={busy || Math.round(days) === saved}>
          {busy ? 'Saving…' : 'Save'}
        </Btn>
        {msg && <span style={{ fontSize: 12, color: msg.startsWith('Error') ? t.danger : t.accent }}>{msg}</span>}
      </div>
    </Card>
  );
}

// ---- Per-track permission overrides (replace semantics) --------------------
// A track override fully replaces a member's project grant on ONE track (can widen or narrow).
// The manager picks a track, then per member chooses "Project default" (no override) or a role
// that applies only on that track. Members carrying an override are shown distinctly. Requires
// `manage` — this panel only renders inside the manage-gated MembersView.

function TrackPermissionsPanel({
  config, session, project, members,
}: {
  config: AppConfig; session: Session; project: Project; members: Member[];
}) {
  const [tracks, setTracks] = useState<ProjectTrack[] | null>(null);
  const [trackId, setTrackId] = useState<string>('');
  const [overrides, setOverrides] = useState<TrackOverride[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the project's tracks once (for the picker).
  useEffect(() => {
    (async () => {
      try { setTracks((await listProjectTracks(config, session, project.projectId)).tracks); }
      catch (e) { setError(String(e)); }
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [project.projectId]);

  // Load overrides whenever the selected track changes.
  const loadOverrides = async (tid: string) => {
    if (!tid) { setOverrides(null); return; }
    setError(null);
    try { setOverrides((await listTrackPermissions(config, session, project.projectId, tid)).overrides); }
    catch (e) { setError(String(e)); }
  };
  useEffect(() => { loadOverrides(trackId); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [trackId]);

  // Map userId → its override on the selected track (if any), for quick lookup per member row.
  const overrideByUser: Record<string, TrackOverride> = {};
  for (const o of overrides || []) overrideByUser[o.userId] = o;

  // Set an override to a named role, or remove it ('' = revert to project default).
  const changeOverride = async (m: Member, value: string) => {
    if (!trackId) return;
    setBusy(true); setError(null);
    try {
      if (value === '') {
        await removeTrackPermission(config, session, project.projectId, trackId, m.userId);
      } else {
        await setTrackPermission(config, session, project.projectId, trackId, m.userId, { role: value as MemberRole });
      }
      await loadOverrides(trackId);
    } catch (e) { setError(String(e)); }
    finally { setBusy(false); }
  };

  const selectedTrack = (tracks || []).find((tr) => tr.trackId === trackId) || null;

  return (
    <Card style={{ padding: 12, marginBottom: 16 }}>
      <strong>Per-track permissions</strong>
      <p style={{ fontSize: 12, color: t.muted, margin: '4px 0 8px' }}>
        Override a member’s access on a single track. An override fully replaces their project role
        for that track — it can grant more or less. “Project default” removes the override.
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, color: t.muted }}>Track:</label>
        <select value={trackId} onChange={(e) => setTrackId(e.target.value)} disabled={!tracks} aria-label="Track for per-track permissions">
          <option value="">— select a track —</option>
          {(tracks || []).map((tr) => (
            <option key={tr.trackId} value={tr.trackId}>{tr.name} ({tr.kind})</option>
          ))}
        </select>
        {tracks && tracks.length === 0 && (
          <span style={{ fontSize: 12, color: t.muted }}>This project has no tracks yet.</span>
        )}
      </div>

      {selectedTrack && overrides !== null && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: `1px solid ${t.border}`, fontSize: 12, color: t.muted }}>
              <th style={{ padding: '6px 0', fontWeight: 600 }}>Member</th>
              <th style={{ fontWeight: 600 }}>Project role</th>
              <th style={{ fontWeight: 600 }}>On this track</th>
              <th style={{ fontWeight: 600 }}>Effective permissions</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => {
              const ov = overrideByUser[m.userId];
              const hasOverride = !!ov;
              const effective = hasOverride ? ov.permissions : m.permissions;
              // Override role for the dropdown: the named role if set that way, else '' so an
              // explicit-perms override still shows as "Custom" via the extra option below.
              const dropdownValue = hasOverride ? (ov.role && MEMBER_ROLES.includes(ov.role) ? ov.role : 'custom') : '';
              return (
                <tr key={m.userId} style={{ borderBottom: `1px solid ${t.border}`, background: hasOverride ? 'rgba(77,184,184,0.10)' : undefined }}>
                  <td style={{ padding: '8px 0' }}>{m.email}</td>
                  <td style={{ color: t.muted, fontSize: 13 }}>{m.role}</td>
                  <td>
                    <select
                      value={dropdownValue}
                      disabled={busy}
                      onChange={(e) => changeOverride(m, e.target.value)}
                      aria-label={`Track permission for ${m.email}`}
                    >
                      <option value="">Project default</option>
                      {/* An explicit-permissions override (role=null) shows as a non-selectable marker. */}
                      {dropdownValue === 'custom' && <option value="custom" disabled>Custom (set via API)</option>}
                      {MEMBER_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td style={{ color: hasOverride ? t.accent : t.faint, fontSize: 12 }}>
                    {effective.join(', ')}
                    {hasOverride && <Badge tone="ok">override</Badge>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {error && <p style={{ color: t.danger, fontSize: 13 }}>{error}</p>}
    </Card>
  );
}

function MembersView({
  config, session, project,
}: {
  config: AppConfig; session: Session; project: Project;
}) {
  const canManage = (project.permissions || []).includes('manage');

  const [members, setMembers] = useState<Member[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<MemberRole>('commenter');
  const [inviteMsg, setInviteMsg] = useState('');

  const myEmail = (session.email || '').toLowerCase();

  const refresh = async () => {
    setError(null);
    try {
      const res = await listMembers(config, session, project.projectId);
      setMembers(res.members);
    } catch (e) { setError(String(e)); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [project.projectId]);

  const invite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    setBusy(true); setInviteMsg('');
    try {
      const res = await inviteMember(config, session, project.projectId, { email, role: inviteRole });
      setInviteMsg(res.newUser
        ? `Invited ${res.email} as ${res.role} (new user created — they can sign in with a magic link).`
        : `Added existing user ${res.email} as ${res.role}.`);
      setInviteEmail('');
      await refresh();
    } catch (e) { setInviteMsg(`Error: ${String(e)}`); }
    finally { setBusy(false); }
  };

  const changeRole = async (m: Member, role: MemberRole) => {
    if (role === m.role) return;
    // Guard against a manager silently demoting themselves out of management.
    if (m.email.toLowerCase() === myEmail && role !== 'manager') {
      if (!window.confirm('This changes YOUR role and may remove your ability to manage this project. Continue?')) {
        await refresh(); // reset the dropdown
        return;
      }
    }
    setBusy(true); setError(null);
    try {
      await setMemberRole(config, session, project.projectId, m.userId, role);
      await refresh();
    } catch (e) { setError(String(e)); }
    finally { setBusy(false); }
  };

  const remove = async (m: Member) => {
    const isSelf = m.email.toLowerCase() === myEmail;
    const msg = isSelf
      ? `Remove YOURSELF from "${project.name}"? You'll lose access to this project.`
      : `Remove ${m.email} from "${project.name}"?`;
    if (!window.confirm(msg)) return;
    setBusy(true); setError(null);
    try {
      await removeMember(config, session, project.projectId, m.userId);
      await refresh();
    } catch (e) { setError(String(e)); }
    finally { setBusy(false); }
  };

  if (!canManage) {
    return <p style={{ color: t.warn }}>You need the “manage” permission on this project to manage it.</p>;
  }

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Manage project · {project.name}</h2>
      <p style={{ fontSize: 12, color: t.muted, margin: '0 0 16px' }}>
        Members, per-track permissions, retention, and the activity log for this project.
      </p>

      {/* Invite */}
      <Card style={{ padding: 12, marginBottom: 16 }}>
        <strong>Invite a member</strong>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="email@example.com"
            aria-label="Invite email address"
            style={{ minWidth: 240 }}
            onKeyDown={(e) => { if (e.key === 'Enter') invite(); }}
          />
          <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as MemberRole)} aria-label="Invite role">
            {MEMBER_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <Btn variant="primary" icon={<Icon.plus />} onClick={invite} disabled={busy || !inviteEmail.trim()}>Invite</Btn>
          <span style={{ fontSize: 12, color: t.muted }}>{ROLE_HINT[inviteRole]}</span>
        </div>
        {inviteMsg && <p style={{ fontSize: 13, color: inviteMsg.startsWith('Error') ? t.danger : t.accent }}>{inviteMsg}</p>}
        <p style={{ fontSize: 12, color: t.muted, margin: '4px 0 0' }}>
          Inviting an email that isn’t a user yet creates their account; an existing user is simply
          added to this project. Sign-in is via emailed magic-link code.
        </p>
      </Card>

      {/* Member list */}
      {members === null && <Muted style={{ display: 'block' }}>Loading…</Muted>}
      {members && members.length > 0 && (
        <Card style={{ padding: 0, marginBottom: 16, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: `1px solid ${t.border}`, fontSize: 12, color: t.muted }}>
                <th style={{ padding: '10px 12px', fontWeight: 600 }}>Email</th>
                <th style={{ padding: '10px 12px', fontWeight: 600 }}>Role</th>
                <th style={{ padding: '10px 12px', fontWeight: 600 }}>Permissions</th>
                <th style={{ padding: '10px 12px' }}></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const isSelf = m.email.toLowerCase() === myEmail;
                return (
                  <tr key={m.userId} style={{ borderBottom: `1px solid ${t.border}` }}>
                    <td style={{ padding: '10px 12px' }}>
                      {m.email}{isSelf ? <Muted style={{ marginLeft: 6 }}>(you)</Muted> : ''}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <select
                        value={MEMBER_ROLES.includes(m.role as MemberRole) ? m.role : ''}
                        aria-label={`Role for ${m.email}`}
                        disabled={busy}
                        onChange={(e) => changeRole(m, e.target.value as MemberRole)}
                      >
                        {!MEMBER_ROLES.includes(m.role as MemberRole) && (
                          <option value="">{m.role}</option>
                        )}
                        {MEMBER_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '10px 12px', color: t.muted, fontSize: 12 }}>{m.permissions.join(', ')}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      <Btn variant="danger" onClick={() => remove(m)} disabled={busy} icon={<Icon.x />} iconOnly title="Remove member" aria-label={`Remove ${m.email}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* Per-project retention window. */}
      <RetentionPanel config={config} session={session} project={project} />

      {/* Per-track permission overrides (only useful once there are members to scope). */}
      {members && members.length > 0 && (
        <TrackPermissionsPanel config={config} session={session} project={project} members={members} />
      )}

      {/* Project activity log (audit trail). */}
      <ActivityPanel config={config} session={session} project={project} />

      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
    </div>
  );
}

// ---- Track detail: versions + player/comments + A/B ------------------------

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

// Friendly labels for the raw version-status enum. The stored value stays as-is (design/data
// model); only the display softens — e.g. REJECTED reads as "Changes requested".
function statusLabel(status: string | null | undefined): string {
  if (!status) return '—';
  const map: Record<string, string> = {
    DRAFT: 'Draft',
    IN_REVIEW: 'In review',
    APPROVED: 'Approved',
    REJECTED: 'Changes requested',
    SUPERSEDED: 'Superseded',
    ARCHIVED: 'Archived',
  };
  return map[status] ?? status;
}

function TrackDetail({
  config, session, project, trackId, trackName,
}: {
  config: AppConfig; session: Session; project: Project; trackId: string; trackName: string;
}) {
  const [versions, setVersions] = useState<VersionSummary[] | null>(null);
  const [currentVersionId, setCurrentVersionId] = useState<string | null>(null);
  const [approvedVersionId, setApprovedVersionId] = useState<string | null>(null);
  // The version currently AUDIBLE in the unified player (drives the review + metadata panels).
  const [activeVersionId, setActiveVersionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Approval + export action state.
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [approvalMsg, setApprovalMsg] = useState('');
  const [exportFiles, setExportFiles] = useState<ExportFile[] | null>(null);

  // "Add a version" upload state.
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState('');
  const [pollVersionId, setPollVersionId] = useState<string | null>(null);

  const refresh = async () => {
    try {
      const res = await listTrackVersions(config, session, project.projectId, trackId);
      setVersions(res.versions);
      setCurrentVersionId(res.currentVersionId);
      setApprovedVersionId(res.approvedVersionId);
    } catch (e) { setError(String(e)); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [trackId]);

  const selected = versions?.find((v) => v.versionId === activeVersionId) ?? null;

  const addVersion = async () => {
    if (!file) return;
    setStatus('Requesting upload URL…');
    try {
      const created = await createVersion(config, session, project.projectId, {
        filename: file.name,
        sizeBytes: file.size,
        trackId, // append as the next version of THIS track
      });
      setStatus(`Uploading v${created.ordinal}…`);
      await uploadToPresignedUrl(created.uploadUrl, file);
      setStatus('Uploaded — analyzing…');
      setFile(null);
      setPollVersionId(created.versionId);
    } catch (e) { setStatus(`Error: ${String(e)}`); }
  };

  // Poll the newly-added version until ingest completes, then refresh the list + select it.
  useEffect(() => {
    if (!pollVersionId) return;
    let stop = false;
    const tick = async () => {
      try {
        const v = await getVersion(config, session, pollVersionId);
        if (stop) return;
        const st = v.assets[0]?.ingestStatus;
        setStatus(`Ingest: ${st}`);
        if (st === 'ready' || st === 'failed') {
          await refresh();
          setPollVersionId(null);
          return;
        }
      } catch (e) { setStatus(`Error: ${String(e)}`); return; }
      if (!stop) setTimeout(tick, 2500);
    };
    tick();
    return () => { stop = true; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [pollVersionId, config, session]);

  const perms = project.permissions || [];
  const canApprove = perms.includes('approve');
  const canRequestChanges = perms.includes('comment');
  const canDownload = perms.includes('download');
  const selectedIsApproved = !!activeVersionId && approvedVersionId === activeVersionId;

  const decide = async (decision: ApprovalDecision) => {
    if (!activeVersionId) return;
    setApprovalBusy(true);
    setApprovalMsg('');
    try {
      const note = decision === 'changes_requested'
        ? (window.prompt('Add a note for the engineer (optional):') ?? undefined)
        : undefined;
      await applyApproval(config, session, activeVersionId, decision, note);
      const label = decision === 'approved' ? 'Approved'
        : decision === 'revoked' ? 'Approval revoked' : 'Changes requested';
      setApprovalMsg(`${label}.`);
      await refresh();
    } catch (e) {
      setApprovalMsg(`Error: ${String(e)}`);
    } finally {
      setApprovalBusy(false);
    }
  };

  const runExport = async () => {
    if (!activeVersionId) return;
    setApprovalBusy(true);
    setApprovalMsg('');
    setExportFiles(null);
    try {
      const res = await exportComments(config, session, activeVersionId);
      setExportFiles(res.files);
      setApprovalMsg(`Exported ${res.commentCount} comment(s). Links expire in ${Math.round(res.expiresInSeconds / 60)} min.`);
    } catch (e) {
      setApprovalMsg(`Error: ${String(e)}`);
    } finally {
      setApprovalBusy(false);
    }
  };

  const canReview = canApprove || canRequestChanges || canDownload;

  return (
    <div>
      <h2>{trackName}</h2>

      {/* Version overview (read-only) — selection to hear happens in the player's A/B/C. */}
      {versions === null && <Muted style={{ display: 'block' }}>Loading…</Muted>}
      {versions && versions.length === 0 && <Muted style={{ display: 'block' }}>No versions yet.</Muted>}
      {versions && versions.length > 0 && (
        <Card style={{ padding: 0, marginBottom: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: `1px solid ${t.border}`, fontSize: 12, color: t.muted }}>
                <th style={{ padding: '10px 12px', fontWeight: 600 }}>Version</th>
                <th style={{ padding: '10px 12px', fontWeight: 600 }}>Created</th>
                <th style={{ padding: '10px 12px', fontWeight: 600 }}>Level</th>
                <th style={{ padding: '10px 12px', fontWeight: 600 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => {
                const isActive = activeVersionId === v.versionId;
                const isCurrent = currentVersionId === v.versionId;
                const isApproved = approvedVersionId === v.versionId;
                const ready = v.primaryAsset?.ingestStatus === 'ready';
                return (
                  <tr
                    key={v.versionId}
                    style={{ borderBottom: `1px solid ${t.border}`, background: isActive ? 'rgba(77,184,184,0.12)' : undefined }}
                  >
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <strong style={{ fontWeight: 600 }}>{v.ordinal != null ? `v${v.ordinal}` : v.versionId.slice(0, 8)}</strong>
                        {v.label ? <span style={{ color: t.muted }}>{v.label}</span> : null}
                        {isCurrent && <Badge tone="neutral">current</Badge>}
                        {isActive && <Badge tone="accent">♪ hearing</Badge>}
                        {isApproved && <Badge tone="ok">✓ approved</Badge>}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', color: t.muted, fontSize: 13 }}>{fmtDate(v.createdAt)}</td>
                    <td style={{ padding: '10px 12px', color: t.muted, fontSize: 13 }}>
                      {v.primaryAsset?.integratedLufs != null ? `${v.primaryAsset.integratedLufs} LU` : '—'}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {v.primaryAsset?.ingestStatus
                        ? <Badge tone={ready ? 'ok' : 'warn'}>{v.primaryAsset.ingestStatus}</Badge>
                        : <Muted>—</Muted>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* Add a version */}
      <Card style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
        <strong style={{ fontSize: 13 }}>Add a version</strong>
        <input type="file" accept=".wav,.aiff,.aif,.flac,.mp3" onChange={(e) => setFile(e.target.files?.[0] ?? null)} aria-label="New version audio file" />
        <Btn variant="primary" onClick={addVersion} disabled={!file}>Upload version</Btn>
        {status && <Muted>{status}</Muted>}
      </Card>

      {/* Review: approval + export, targeting the version currently AUDIBLE in the player. */}
      {canReview && activeVersionId && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 13 }}>Review</strong>
            {selected?.ordinal != null && <Muted>v{selected.ordinal}</Muted>}
            {selectedIsApproved
              ? <Badge tone="ok">✓ Approved</Badge>
              : selected?.status ? <Badge tone="draft">{statusLabel(selected.status)}</Badge> : null}
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {canApprove && !selectedIsApproved && (
              <Btn variant="primary" icon={<Icon.check />} onClick={() => decide('approved')} disabled={approvalBusy}>Approve this version</Btn>
            )}
            {canApprove && selectedIsApproved && (
              <Btn variant="secondary" onClick={() => decide('revoked')} disabled={approvalBusy}>Revoke approval</Btn>
            )}
            {canRequestChanges && (
              <Btn variant="secondary" onClick={() => decide('changes_requested')} disabled={approvalBusy}>Request changes</Btn>
            )}
            {canDownload && (
              <Btn variant="ghost" icon={<Icon.download />} onClick={runExport} disabled={approvalBusy}>Export comments</Btn>
            )}
          </div>
          {approvalMsg && <p style={{ fontSize: 13, color: approvalMsg.startsWith('Error') ? t.danger : t.muted }}>{approvalMsg}</p>}
          {exportFiles && exportFiles.length > 0 && (
            <ul style={{ fontSize: 13, marginTop: 4 }}>
              {exportFiles.map((f) => (
                <li key={f.name}>
                  <a href={f.url} target="_blank" rel="noreferrer">{f.name}</a>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* Unified player: one transport (waveform + playhead) with an A/B/C version switch and
          the comment panel wired to whichever version is currently audible. */}
      <TrackPlayer
        config={config}
        session={session}
        projectId={project.projectId}
        trackId={trackId}
        loudnessRef={project.loudnessRef}
        canComment={canRequestChanges}
        onApprovalContextChange={setActiveVersionId}
      />

      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
    </div>
  );
}

// ---- Stems detail: stem sets (multi-asset versions) + per-stem ingest ------

// One stem set's versions, each showing its stems and per-stem ingest status. Overdubs are added
// to the CURRENT (latest) version, extending the set rather than cutting a new version. Playback
// mixing (per-stem solo/mute) is task 25 — this view is upload + list + ingest status.
function StemsView({
  config, session, project, trackId, trackName,
}: {
  config: AppConfig; session: Session; project: Project; trackId: string; trackName: string;
}) {
  const canUpload = (project.permissions || []).includes('upload_version');
  const [versions, setVersions] = useState<VersionSummary[] | null>(null);
  const [currentVersionId, setCurrentVersionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [stemFiles, setStemFiles] = useState<File[]>([]);
  const [status, setStatus] = useState('');
  const [pollVersionId, setPollVersionId] = useState<string | null>(null);
  const [notices, setNotices] = useState<PrecheckNotice[]>([]);
  // Stem management: selection (for download), busy flag, and per-action message.
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [manageBusy, setManageBusy] = useState(false);
  const [manageMsg, setManageMsg] = useState('');

  const canManage = (project.permissions || []).includes('manage');
  const canDownload = (project.permissions || []).includes('download');
  const myEmail = (session.email || '').toLowerCase();

  const refresh = async () => {
    try {
      const res = await listTrackVersions(config, session, project.projectId, trackId);
      setVersions(res.versions);
      setCurrentVersionId(res.currentVersionId);
    } catch (e) { setError(String(e)); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [trackId]);

  // The current version is where overdubs land (newest is first — versions sort newest-ordinal).
  const currentVersion = versions?.find((v) => v.versionId === currentVersionId) ?? versions?.[0] ?? null;

  // On select: compare each new file against the CURRENT set's stems (SR/bit-depth via WAV header)
  // + recommend WAV. Advisory only; the authoritative length verdict comes post-ingest.
  const onSelectAddStems = async (files: File[]) => {
    setStemFiles(files);
    setNotices([]);
    if (files.length === 0) return;
    const setStems: SetStemRef[] = (currentVersion?.assets || []).map((a) => ({
      sampleRateHz: a.sampleRateHz,
      bitDepth: a.bitDepth,
      channels: a.channels,
      durationMs: a.durationMs,
      sizeBytes: a.sizeBytes, // real declared byte size → enables the size-vs-length proxy
    }));
    const out: PrecheckNotice[] = [];
    for (const f of files) {
      const header = isWav(f.name) ? await readWavHeader(f) : null;
      buildPrecheckNotices(f, header, setStems).forEach((n) => out.push(n));
    }
    setNotices(out);
  };

  const addStems = async () => {
    if (stemFiles.length === 0 || !currentVersion) return;
    setStatus('Requesting upload URLs…');
    try {
      const stems: StemUpload[] = stemFiles.map((f) => ({ filename: f.name, sizeBytes: f.size }));
      const res = await addStemsToVersion(config, session, currentVersion.versionId, stems);
      const byName = new Map(stemFiles.map((f) => [f.name, f]));
      setStatus(`Uploading ${res.assets.length} stem(s)…`);
      await Promise.all(
        res.assets.map((a) => {
          const f = byName.get(a.filename);
          return f ? uploadToPresignedUrl(a.uploadUrl, f) : Promise.resolve();
        })
      );
      setStemFiles([]);
      setNotices([]);
      setPollVersionId(currentVersion.versionId);
      setStatus('Uploaded — analyzing…');
    } catch (e) { setStatus(`Error: ${String(e)}`); }
  };

  // Whether the caller may delete a given stem: managers delete any; others only their own
  // uploads. The API enforces this authoritatively (by sub); this just gates the button (by
  // email, since the client session carries email not sub). A stem with no uploader = manager-only.
  const canDeleteStem = (a: VersionAsset) =>
    canManage || (!!a.uploadedByEmail && a.uploadedByEmail.toLowerCase() === myEmail);

  // One-click "Download selected": fetch a presigned original-file URL per selected stem and
  // trigger the browser download for each. (No ZIP — the browser may prompt once to allow
  // multiple downloads for a large selection.)
  const downloadSelected = async (version: VersionSummary) => {
    const ids = version.assets.filter((a) => selected[a.assetId] && a.ingestStatus === 'ready').map((a) => a.assetId);
    if (ids.length === 0) { setManageMsg('Select at least one ready stem to download.'); return; }
    setManageBusy(true); setManageMsg(`Preparing ${ids.length} download(s)…`);
    try {
      for (const assetId of ids) {
        const { url, filename } = await getStemDownloadUrl(config, session, version.versionId, assetId);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || '';
        document.body.appendChild(a);
        a.click();
        a.remove();
        // Small stagger so the browser treats them as distinct downloads.
        await new Promise((r) => setTimeout(r, 300));
      }
      setManageMsg(`Started ${ids.length} download(s).`);
    } catch (e) { setManageMsg(`Error: ${String(e)}`); }
    finally { setManageBusy(false); }
  };

  const deleteStem = async (version: VersionSummary, a: VersionAsset) => {
    const name = a.stemName || a.originalFilename || a.assetId.slice(0, 8);
    if (!window.confirm(`Delete stem "${name}"? This removes the file permanently.`)) return;
    setManageBusy(true); setManageMsg('');
    try {
      await deleteStemAsset(config, session, version.versionId, a.assetId);
      setSelected((prev) => { const n = { ...prev }; delete n[a.assetId]; return n; });
      await refresh();
      setManageMsg(`Deleted "${name}".`);
    } catch (e) { setManageMsg(`Error: ${String(e)}`); }
    finally { setManageBusy(false); }
  };

  // Poll the version until every stem finishes ingesting (all ready or failed), then refresh.
  useEffect(() => {
    if (!pollVersionId) return;
    let stop = false;
    const tick = async () => {
      try {
        const v = await getVersion(config, session, pollVersionId);
        if (stop) return;
        const statuses = v.assets.map((a) => a.ingestStatus);
        const pending = statuses.filter((s) => s !== 'ready' && s !== 'failed').length;
        setStatus(pending > 0 ? `Analyzing ${pending} stem(s)…` : 'All stems analyzed.');
        if (pending === 0) {
          await refresh();
          setPollVersionId(null);
          return;
        }
      } catch (e) { setStatus(`Error: ${String(e)}`); return; }
      if (!stop) setTimeout(tick, 2500);
    };
    tick();
    return () => { stop = true; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [pollVersionId, config, session]);

  return (
    <div>
      <h2>{trackName} <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 'normal' }}>· stem set</span></h2>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 8px' }}>
        Stems are checked against each other after analysis — a red flag means a format mismatch
        (sample rate / bit depth), amber is a heads-up (clipping, or a length that differs from the
        longest stem). Nothing is blocked; the flags prompt you to confirm.
      </p>

      {/* Add overdubs to the current version */}
      {canUpload && currentVersion && (
        <Card style={{ margin: '12px 0 16px', display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
          <strong style={{ fontSize: 13 }}>
            Add stems to the current set
            {currentVersion.ordinal != null && <Muted style={{ marginLeft: 6 }}>v{currentVersion.ordinal}</Muted>}
          </strong>
          <Muted style={{ display: 'block' }}>
            Overdubs (vocals, a solo…) extend this set — they don’t create a new version.
            We recommend WAV (lossless, exact length).
          </Muted>
          <input
            type="file" multiple accept=".wav,.aiff,.aif,.flac,.mp3"
            onChange={(e) => onSelectAddStems(Array.from(e.target.files ?? []))}
            aria-label="Add stem audio files"
          />
          {stemFiles.length > 0 && (
            <Muted>
              {stemFiles.length} file{stemFiles.length === 1 ? '' : 's'}: {stemFiles.map((f) => f.name).join(', ')}
            </Muted>
          )}
          <PrecheckNotices notices={notices} />
          <Btn variant="primary" icon={<Icon.plus />} onClick={addStems} disabled={stemFiles.length === 0}>Add stems</Btn>
          {status && <Muted>{status}</Muted>}
        </Card>
      )}

      {versions === null && <Muted style={{ display: 'block' }}>Loading…</Muted>}
      {versions && versions.length === 0 && <Muted style={{ display: 'block' }}>No stem sets yet.</Muted>}
      {versions && versions.map((v) => {
        const isCurrent = (currentVersionId ?? versions[0]?.versionId) === v.versionId;
        const hasReady = v.assets.some((a) => a.ingestStatus === 'ready');
        return (
          <Card key={v.versionId} style={{ padding: 12, marginBottom: 12 }}>
            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <strong>{v.ordinal != null ? `v${v.ordinal}` : v.versionId.slice(0, 8)}</strong>
              {v.label ? <span style={{ color: t.muted }}>{v.label}</span> : null}
              {isCurrent && <Badge tone="neutral">current</Badge>}
              <Muted>{v.assetCount} stem{v.assetCount === 1 ? '' : 's'} · {fmtDate(v.createdAt)}</Muted>
              {v.techCheckSummary && v.techCheckSummary.errors > 0 && (
                <Badge tone="danger">⚑ {v.techCheckSummary.errors} issue{v.techCheckSummary.errors === 1 ? '' : 's'}</Badge>
              )}
              {v.techCheckSummary && v.techCheckSummary.warns > 0 && (
                <Badge tone="warn">⚠ {v.techCheckSummary.warns} warning{v.techCheckSummary.warns === 1 ? '' : 's'}</Badge>
              )}
            </div>

            {/* CURRENT version: ONE unified table — playback (mute/solo/level) + file controls
                (select/download/delete) on each row. The player owns the audio graph; we pass the
                file-control wiring in. Older versions aren't in the audio graph, so they show the
                file-only table below. */}
            {isCurrent && hasReady ? (
              <StemsPlayer
                config={config}
                session={session}
                projectId={project.projectId}
                versionId={v.versionId}
                assets={v.assets}
                fileControls={{
                  canDownload,
                  selected,
                  onToggleSelect: (assetId, checked) => setSelected((prev) => ({ ...prev, [assetId]: checked })),
                  canDelete: canDeleteStem,
                  onDelete: (a) => deleteStem(v, a),
                  onDownload: () => downloadSelected(v),
                  busy: manageBusy,
                }}
              />
            ) : (
              <>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: `1px solid ${t.border}`, fontSize: 12, color: t.muted }}>
                      {canDownload && <th style={{ padding: '4px 0', width: 24 }}></th>}
                      <th style={{ padding: '4px 0' }}>Stem</th>
                      <th>Format</th>
                      <th>Length</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {v.assets.map((a) => {
                      const errs = a.techChecks.filter((c) => c.severity === 'error');
                      const warns = a.techChecks.filter((c) => c.severity === 'warn');
                      const isReady = a.ingestStatus === 'ready';
                      const colCount = canDownload ? 6 : 5;
                      return (
                        <Fragment key={a.assetId}>
                          <tr style={{ borderBottom: a.techChecks.length ? 'none' : `1px solid ${t.border}` }}>
                            {canDownload && (
                              <td style={{ padding: '6px 0' }}>
                                <input
                                  type="checkbox"
                                  checked={!!selected[a.assetId]}
                                  disabled={!isReady}
                                  onChange={(e) => setSelected((prev) => ({ ...prev, [a.assetId]: e.target.checked }))}
                                  title={isReady ? 'Select for download' : 'Not ready'}
                                />
                              </td>
                            )}
                            <td style={{ padding: '6px 0' }}>
                              {a.stemName || a.originalFilename || a.assetId.slice(0, 8)}
                              {errs.length > 0 && <span title="format mismatch" style={{ marginLeft: 6, color: 'var(--danger)' }}>⚑</span>}
                              {warns.length > 0 && <span title="warning" style={{ marginLeft: 4, color: 'var(--warn)' }}>⚠</span>}
                              {a.uploadedByEmail && (
                                <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-faint)' }}>· {a.uploadedByEmail}</span>
                              )}
                            </td>
                            <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                              {a.sampleRateHz ? `${(a.sampleRateHz / 1000).toFixed(1)} kHz` : '—'}
                              {a.bitDepth ? ` / ${a.bitDepth}-bit` : ''}
                              {a.channels ? ` / ${a.channels === 1 ? 'mono' : a.channels === 2 ? 'stereo' : `${a.channels}ch`}` : ''}
                            </td>
                            <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                              {a.durationMs != null ? `${(a.durationMs / 1000).toFixed(1)}s` : '—'}
                            </td>
                            <td>
                              <Badge tone={a.ingestStatus === 'ready' ? 'ok' : a.ingestStatus === 'failed' ? 'danger' : 'warn'}>
                                {a.ingestStatus}
                              </Badge>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {canDeleteStem(a) && (
                                <Btn
                                  variant="danger"
                                  onClick={() => deleteStem(v, a)}
                                  disabled={manageBusy}
                                  icon={<Icon.x />}
                                  iconOnly
                                  title="Delete this stem"
                                  aria-label={`Delete stem ${a.stemName || a.originalFilename || a.assetId.slice(0, 8)}`}
                                />
                              )}
                            </td>
                          </tr>
                          {a.techChecks.length > 0 && (
                            <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                              <td colSpan={colCount} style={{ padding: '0 0 8px 0' }}>
                                {a.techChecks.map((c, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      fontSize: 12,
                                      color: c.severity === 'error' ? 'var(--danger)' : 'var(--warn)',
                                      paddingLeft: 4,
                                    }}
                                  >
                                    {c.severity === 'error' ? '⚑' : '⚠'} {c.message}
                                  </div>
                                ))}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
                {canDownload && hasReady && (
                  <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Btn variant="secondary" icon={<Icon.download />} onClick={() => downloadSelected(v)} disabled={manageBusy}>
                      Download selected
                    </Btn>
                    <Muted>Tick stems above, then download the original files.</Muted>
                  </div>
                )}
              </>
            )}
          </Card>
        );
      })}

      {manageMsg && (
        <p style={{ fontSize: 13, color: manageMsg.startsWith('Error') ? t.danger : t.accent }}>{manageMsg}</p>
      )}

      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
    </div>
  );
}

// ---- Sign in ---------------------------------------------------------------

function SignIn({ config, onSignedIn }: { config: AppConfig; onSignedIn: (s: Session) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submitCodeRef = useRef<((c: string) => Promise<Session>) | null>(null);
  const [awaitingCode, setAwaitingCode] = useState(false);

  const doPassword = async () => {
    setErr(null); setPending(true);
    try { onSignedIn(await signInWithPassword(config, email, password)); }
    catch (e) { setErr(String(e)); }
    finally { setPending(false); }
  };

  const doMagicLink = () => {
    setErr(null);
    try {
      const { submitCode } = beginPasswordless(config, email);
      submitCodeRef.current = submitCode;
      setAwaitingCode(true);
    } catch (e) { setErr(String(e)); }
  };

  const doSubmitCode = async () => {
    if (!submitCodeRef.current) return;
    setErr(null); setPending(true);
    try { onSignedIn(await submitCodeRef.current(code)); }
    catch (e) { setErr(String(e)); }
    finally { setPending(false); }
  };

  const inputStyle: React.CSSProperties = { width: '100%', marginTop: 4 };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 13, color: t.muted, marginBottom: 12 };

  return (
    <div style={{ maxWidth: 420, margin: '0 auto' }}>
      {/* Intro: what this is, for anyone who lands here without context. */}
      <Card style={{ marginBottom: 16 }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Welcome to {BRAND_NAME}</h2>
        <p style={{ margin: 0, fontSize: 14, color: t.muted, lineHeight: 1.55 }}>
          This is our space to collaborate. You can listen to and comment on mixes, upload additional
          parts, and listen to or download stems. If you have an active project and can’t find your
          login details, <a href="mailto:info@bridgehousecreative.com" style={{ color: t.accent }}>drop me a note</a>.
        </p>
      </Card>

      <Card style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Sign in</h3>
        <label style={labelStyle}>
          Email
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" style={inputStyle} />
        </label>
        <label style={labelStyle}>
          Password
          <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" style={inputStyle} />
        </label>
        <Btn variant="primary" disabled={pending || !email || !password} onClick={doPassword}>Sign in</Btn>

        <div style={{ borderTop: `1px solid ${t.border}`, margin: '16px 0 12px' }} />

        <Muted style={{ display: 'block', marginBottom: 8 }}>Or email me a one-time code</Muted>
        {!awaitingCode ? (
          <Btn variant="secondary" disabled={!email} onClick={doMagicLink}>Send code</Btn>
        ) : (
          <>
            <label style={labelStyle}>
              Code
              <input value={code} onChange={(e) => setCode(e.target.value)} style={inputStyle} />
            </label>
            <Btn variant="secondary" disabled={pending || !code} onClick={doSubmitCode}>Verify code</Btn>
          </>
        )}
        {err && <p className="error" style={{ color: t.danger, marginTop: 12, marginBottom: 0 }}>{err}</p>}
      </Card>
    </div>
  );
}
